import OnlineService from '../online';
import OfflineGetService from '../offlineGet';
import OfflinePostService from '../offlinePost';
import store from '../../store';
import { cleanData } from './patch';

import { API_TYPE } from '../../constants';

const onlineService = new OnlineService();
const offlineGetService = new OfflineGetService();
const offlinePostService = new OfflinePostService();
const offlinePostStatusCode = 503;
const offlinePostStatusText = 'offline storage used';
const navigatorStatusOfflineText = 'navigator is offline';

/**
     * Get key for offline storage
     * @param  {[type]} url    [description]
     * @param  {[type]} params [description]
     * @return {[type]}        [description]
     */
const getOfflineKey = function (url, params) {
    return `${url}:${JSON.stringify(params)}`;
};

/**
     * is offline use of data allowed?
     * @return {boolean} [true if offline data is allowed, false otherwise]
     */
const offlineAllowed = function() {
    // Offline storage afronden na deze release
    // Komende release: offline storage niet toestaan.
    return false;
};

/**
     * is offline post of data allowed?
     * @return {boolean} [true if offline data is allowed, false otherwise]
     */
const offlinePostAllowed = function(url) {
    return false;

};

/**
 * Get either the API or WEB-API endpoint
 * @param {any} apiType
 */
const getEndpoint = (apiType) => {
    // first handle explicit apiType parameters
    if (apiType === API_TYPE.API) {
        return store.state.appConfig[API_TYPE.API].endpoint;
    } else if (apiType?.toLowerCase() === API_TYPE.WEBAPI.toLowerCase()) {
        return store.state.appConfig[API_TYPE.WEBAPI]?.endpoint || store.state.appConfig.webapi.endpoint;
    }
    // otherwise default endpoint
    return store.getters.endpoint;
};

/**
     * Api Get call
     * @param  {string} url api url
     * @param  {object} params querystring
     * @param  {string} apiType api or webapi endpoint
     * @param  {number} timeout custom request timeout, defaults to 10000 ms
     * @return {*}     [description]
     */
const proxyGet = function (url, params, apiType = API_TYPE.NONE, timeout = 0) {
    const apiEndpoint = getEndpoint(apiType);
    return new Promise((resolve, reject) => {
        try {
            if (offlineAllowed()) {
                const offlineKey = getOfflineKey(url, params);
                // offline first!
                offlineGetService.getData(offlineKey).then((result) => {
                    if (result && !window.navigator.onLine) {
                        // check data is expired or is offline
                        if (new Date() < new Date(result.expires) || !window.navigator.onLine) {
                            resolve({ data: result.data });
                            return;
                        }
                    }
                    // get online data of no offline data is found
                    onlineService.axiosGet(url, params, apiEndpoint, timeout).then((response) => {
                        // add to offline storage, but stringify here in case of errors for enriched data (f.i. themelayers)
                        // see: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Cyclic_object_value
                        offlineGetService.setData(offlineKey, JSON.stringify(response.data));
                        resolve(response);
                    });
                });
            } else {
                // allways online
                onlineService.axiosGet(url, params, apiEndpoint, timeout).then((response) => {
                    // do not store offline
                    resolve(response);
                }, (error) => {
                    reject(error);
                });
            }
        } catch (error) {
            reject(error);
        }
    });
};

/**
     * Api Post call
     * @param  {string} url api url
     * @param  {object} data data to post
     * @param  {string} apiType api or webapi endpoint
     * @param  {number} timeout custom request timeout, defaults to 10000 ms
     * @return {*}     [description]
     */
const proxyPost = function (url, params, apiType = API_TYPE.NONE, timeout = 0, config = null, fixIds = false) {
    const apiEndpoint = getEndpoint(apiType);

    let data;
    if (fixIds) {
        data = cleanData(params);
    }

    return new Promise((resolve, reject) => {
        try {
            if (offlinePostAllowed(url)) {
                // are we offline?
                if (!window.navigator.onLine) {
                    // offline post data
                    offlinePostService.setData(url, JSON.stringify(data || params)).then(() => {
                        // send event post data stored offline
                        resolve({
                            'url': url,
                            'status': offlinePostStatusCode,
                            'statusText': offlinePostStatusText
                        });
                    }, (error) => {
                        // error store offline post data
                        reject(error);
                    });
                } else {
                    // online post data
                    onlineService.axiosPost(url, data || params, apiEndpoint, timeout, config).then((response) => {
                        resolve(response);
                    }, (error) => {
                        reject(error);
                    });
                }
            } else {
                onlineService.axiosPost(url, data || params, apiEndpoint, timeout, config).then((response) => {
                    resolve(response);
                }, (error) => {
                    reject(error);
                });
            }
        } catch (error) {
            reject(error);
        }
    });
};

/**
     * Api Put call
     * @param  {string} url api url
     * @param  {object} data data to post
     * @param  {string} apiType api or webapi endpoint
     * @param  {number} timeout custom request timeout, defaults to 10000 ms
     * @return {*}     [description]
     */
const proxyPut = function (url, params, apiType = API_TYPE.NONE, timeout = 0, config = null, fixIds = false) {
    const apiEndpoint = getEndpoint(apiType);

    let data;
    if (fixIds) {
        data = cleanData(params);
    }

    return new Promise((resolve, reject) => {
        try {

            onlineService.axiosPut(url, data || params, apiEndpoint, timeout, config).then((response) => {
                resolve(response);
            }, (error) => {
                reject(error);
            });

        } catch (error) {
            reject(error);
        }
    });
};

/**
     * Api Delete call
     * Delete is allways online.
     * @param  {string} url api url
     * @param  {object} params data to post
     * @param  {string} apiType api or webapi endpoint
     * @return {*}     [description]
     */
const proxyDelete = function (url, params, timeout, apiType = API_TYPE.NONE) {
    const apiEndpoint = getEndpoint(apiType);
    return new Promise((resolve, reject) => {
        try {
            onlineService.axiosDelete(url, params, apiEndpoint, timeout).then((response) => {
                resolve(response);
            });
        } catch (error) {
            reject(error);
        }
    });
};

/**
     * Get the number of offline requests.
     * @return {object} [promise object]
     */
const numberOfflineRequests = function () {
    return new Promise((resolve, reject) => {
        try {
            // get all keys of the offline stored post requests
            offlinePostService.getAllKeys().then((keys) => {
                resolve(keys.length);
            }, (error) => {
                reject(error);
            });
        } catch (error) {
            reject(error);
        }
    });
};

/**
     * Send offline stored post requests.
     * All ofline stored post request are send.
     * @return {object} [promise object]
     */
const postOfflineRequests = async function () {
    // are we offline?
    if (!window.navigator.onLine) {
        throw new Error(navigatorStatusOfflineText);
    }
    // get all keys of the offline stored post requests
    const keys = await offlinePostService.getAllKeys();
    while (keys.length > 0) {
        const key = keys.shift();
        // get offline data for key
        const getResult = await offlinePostService.getData(key);
        if (getResult) {
            await proxyPost(getResult.url, getResult.data);
            // delete record from offline storage
            await offlinePostService.delData(key);
            if (keys.length === 0) {
                // done
                return true;
            }
        }

    }
    return false;
};

// revealing public API
const createNedGlobeApiService = {
    get: proxyGet,
    post: proxyPost,
    put: proxyPut,
    delete: proxyDelete,
    getData: proxyGet,
    postData: proxyPost,
    deleteData: proxyDelete,
    // offline data functions
    numberOfflineRequests: numberOfflineRequests,
    postOfflineRequests: postOfflineRequests,

    get offlinePostStatusCode() {
        return offlinePostStatusCode;
    },

    get offlinePostStatusText() {
        return offlinePostStatusText;
    },

    get navigatorStatusOfflineText() {
        return navigatorStatusOfflineText;
    },

    /**
         * Is authentication required for backend?
         * Yes => Mobile/Dashboard application
         * No => Web application
         * @return {[type]} [description]
         */
    get requiresAuthentication() {
        return store.state.requiresAuthentication;
    },

    get endpoint() {
        return getEndpoint();
    }
};

export default createNedGlobeApiService;
